import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import imageUrlBuilder from '@sanity/image-url';
import './PaisajesUrbanos.css';
import { analytics } from '../../firebase/firebase';
import { logEvent } from 'firebase/analytics';

const builder = imageUrlBuilder(client);
const urlFor = (source) => builder.image(source);

const CATEGORIA = 'paisajesurbanos';
const TITLE = 'Paisajes Urbanos';
const DESCRIPCION = `"Durante mucho tiempo, mi amor por la naturaleza me alejó de la fotografía de ciudad. Hasta que descubrí su encanto: la esencia visual, social y cultural que late en sus calles. Busco capturar formas y escenas que revelen cómo vivimos, construimos y coexistimos en estos espacios artificiales, con su propio ritmo, su historia y sus contrastes."`;

const SUBCATS = [
  { label: 'Arquitectura', path: '/galeria/paisajes-urbanos/arquitectura', key: 'arquitectura' },
  { label: 'Urbanismo', path: '/galeria/paisajes-urbanos/urbanismo', key: 'urbanismo' },
];

const PAGE_SIZE = 10;

// storage keys
const GRID_STATE_KEY = (pathname, search) => `gridState:${pathname}${search || ''}`;
const ORDER_STATE_KEY = (pathname) => `gridOrder:${pathname}`;

// favoritos / login
const readFavs = () => { try { return new Set(JSON.parse(localStorage.getItem('favoritos') || '[]')); } catch { return new Set(); } };
const saveFavs = (set) => localStorage.setItem('favoritos', JSON.stringify([...set]));
const isLoggedIn = () =>
  Boolean(
    localStorage.getItem('authUser') ||
    localStorage.getItem('user') ||
    localStorage.getItem('uid') ||
    localStorage.getItem('isLoggedIn') ||
    sessionStorage.getItem('authUser')
  );

// orientación / layout
const ORIENT = { V:'V', H:'H' };
function fromAsset(imgField){
  const asset=imgField?.asset; if(!asset) return null;
  const url = asset.url || urlFor(imgField).width(1200).auto('format').url();
  const dims=asset.metadata?.dimensions; const w=dims?.width??null, h=dims?.height??null;
  let orientation=null; if(w&&h) orientation = h>w ? ORIENT.V : ORIENT.H;
  return { url, w, h, orientation };
}
function normalizeDocs(docs){
  return docs.map(d=>{
    const primary=fromAsset(d.previewImage) || fromAsset(d.detalleImage);
    const imageUrl=primary?.url||null; let orientation=primary?.orientation??null;
    let ratio=null;
    if(primary?.w && primary?.h) ratio=primary.w/primary.h;
    else{
      const ar=d.previewImage?.asset?.metadata?.dimensions?.aspectRatio ?? d.detalleImage?.asset?.metadata?.dimensions?.aspectRatio ?? null;
      if(typeof ar==='number'){ ratio=ar; orientation=orientation || (ar<1?ORIENT.V:ORIENT.H); }
    }
    return { _id:d._id, titulo:d.titulo||'', imageUrl, orientation, ratio, createdAt:d._createdAt||null, subcategoria:(d.subcategoria||'').toLowerCase() };
  }).filter(x=>!!x.imageUrl);
}
async function ensureOrientation(items){
  const unresolved=items.filter(it=>!it.orientation||!it.ratio);
  await Promise.all(unresolved.map(it=>new Promise((resolve)=>{
    const img=new Image();
    img.onload=()=>{ const nw=img.naturalWidth||1, nh=img.naturalHeight||1;
      if(!it.orientation) it.orientation = nh>nw ? ORIENT.V : ORIENT.H;
      if(!it.ratio) it.ratio = nw/nh; resolve(); };
    img.onerror=()=>resolve(); img.src=it.imageUrl;
  })));
  return items;
}
function shuffle(items){ const a=[...items]; for(let i=a.length-1;i>0;i--){ const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]]; } return a; }
function buildRows(items){
  const V=items.filter(i=>i.orientation===ORIENT.V), H=items.filter(i=>i.orientation===ORIENT.H);
  const rows=[], patterns=['VHH','HVH','HHV']; let p=0; const v=[...V], h=[...H]; const take=a=>a.length?a.shift():null;
  while(v.length||h.length){
    const pat=patterns[p%patterns.length];
    if(pat==='VHH'&&v.length>=1&&h.length>=2) rows.push([take(v),take(h),take(h)].filter(Boolean));
    else if(pat==='HVH'&&h.length>=1&&v.length>=1&&h.length>=1) rows.push([take(h),take(v),take(h)].filter(Boolean));
    else if(pat==='HHV'&&h.length>=2&&v.length>=1) rows.push([take(h),take(h),take(v)].filter(Boolean));
    else if(h.length>=3) rows.push([take(h),take(h),take(h)].filter(Boolean));
    else if(v.length>=1&&h.length>=1) rows.push([take(v),take(h),take(h)||take(v)||null].filter(Boolean));
    else { const last=[take(v)||take(h),take(v)||take(h),take(v)||take(h)].filter(Boolean); if(last.length) rows.push(last); break; }
    p++;
  }
  return rows.filter(r=>r.length>0);
}
function computeImageBox(cardEl, ratio){
  const fallback={w:0,h:0}; if(!cardEl) return fallback;
  const Wc=cardEl.clientWidth||cardEl.getBoundingClientRect().width||0;
  const Hc=cardEl.clientHeight||cardEl.getBoundingClientRect().height||0;
  if(!Wc||!Hc) return fallback;
  const r=Number(ratio)>0?Number(ratio):1; const c=Wc/Hc;
  if(c>r){ const imgW=Hc*r; return {w:Math.round(imgW), h:Math.round(Hc)}; }
  const imgH=Wc/r; return {w:Math.round(Wc), h:Math.round(imgH)};
}

/* búsqueda fuzzy */
const _norm=(s)=>(s||'').toString().normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase();
function _lev(a,b){a=_norm(a);b=_norm(b);const m=a.length,n=b.length;if(!m)return n;if(!n)return m;const dp=Array(n+1);for(let j=0;j<=n;j++)dp[j]=j;for(let i=1;i<=m;i++){let prev=dp[0];dp[0]=i;for(let j=1;j<=n;j++){const tmp=dp[j];const c=a[i-1]===b[j-1]?0:1;dp[j]=Math.min(dp[j]+1,dp[j-1]+1,prev+c);prev=tmp;}}return dp[n];}
const fuzzyIncludes=(needle,hay)=>{
  const n=_norm(needle).trim(), h=_norm(hay);
  if(!n) return true;
  if(h.includes(n)) return true;
  const words=n.split(/\s+/).filter(Boolean);
  if(words.length && words.every(w=>h.includes(w))) return true;
  const dist=_lev(n,h); const thr=Math.max(2,Math.floor(n.length*.25));
  return dist<=thr;
};

const PaisajesUrbanos = () => {
  const history=useHistory();
  const location=useLocation();

  const initial = (()=> {
    const params=new URLSearchParams(location.search);
    const urlSort=params.get('sort');
    const urlPage=parseInt(params.get('page')||'1',10);
    const key=GRID_STATE_KEY(location.pathname, location.search);
    let saved=null; try{ saved=JSON.parse(sessionStorage.getItem(key)||'null'); }catch{}
    const sort=(saved?.sortKey && ['random','recent','az','za','liked'].includes(saved.sortKey)) ? saved.sortKey :
                (urlSort && ['random','recent','az','za','liked'].includes(urlSort)) ? urlSort : 'random';
    const page=(typeof saved?.page==='number' && saved.page>=1) ? saved.page : (!Number.isNaN(urlPage)&&urlPage>=1?urlPage:1);
    const query = typeof saved?.query==='string' ? saved.query : '';
    return { sortKey:sort, page, query, restoreY: typeof saved?.y==='number'?saved.y:null, lastId:saved?.lastId||null };
  })();

  const [sortKey,setSortKey]=useState(()=>initial.sortKey);
  const [page,setPage]=useState(()=>initial.page);
  const [query,setQuery]=useState(()=>initial.query||'');

  const [rawItems,setRawItems]=useState([]);
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState(null);
  const [favoritos,setFavoritos]=useState(new Set());
  const [showLogin,setShowLogin]=useState(false);

  const startRef=useRef(Date.now());
  const itemsCountRef=useRef(0);

  const rawIndexRef=useRef({});
  const savedOrderRef=useRef(null);

  const cardsRef=useRef({});
  const ratioRef=useRef({});
  const [imgBox,setImgBox]=useState({});
  const roRef=useRef(null);

  const restoreOnceRef=useRef(false);
  const desiredYRef=useRef(initial.restoreY);
  const pendingIdRef=useRef(initial.lastId);
  const restoreTriesRef=useRef(0);
  const restoringSortRef=useRef(true);
  const restoringQueryRef=useRef(true);

  useLayoutEffect(()=>{
    roRef.current=new ResizeObserver((entries)=>{
      const patch={};
      for(const entry of entries){
        const el=entry.target; const id=el.getAttribute('data-id'); if(!id) continue;
        const ratio=ratioRef.current[id]||1; patch[id]=computeImageBox(el, ratio);
      }
      if(Object.keys(patch).length) setImgBox(prev=>({...prev, ...patch}));
    });
    return ()=> roRef.current?.disconnect();
  },[]);
  useEffect(()=>{
    const ro=roRef.current;
    Object.values(cardsRef.current).forEach(el=>el&&ro?.unobserve(el));
    Object.entries(cardsRef.current).forEach(([id,el])=>el&&ro?.observe(el));
    const patch={};
    Object.entries(cardsRef.current).forEach(([id,el])=>{
      const ratio=ratioRef.current[id]||1; patch[id]=computeImageBox(el, ratio);
    });
    if(Object.keys(patch).length) setImgBox(prev=>({...prev, ...patch}));
    return ()=>{ Object.values(cardsRef.current).forEach(el=>el&&ro?.unobserve(el)); };
  },[rows]);

  useEffect(()=>()=>{ const duration=Date.now()-startRef.current; if(analytics) logEvent(analytics,'paisajesurbanos_engagement',{duration_ms:duration, filas:rows.length, items_total:itemsCountRef.current||0}); },[rows.length]);
  useEffect(()=>{ setFavoritos(readFavs()); },[]);

  useEffect(()=>{ let prev=null; try{ if('scrollRestoration' in window.history){ prev=window.history.scrollRestoration; window.history.scrollRestoration='manual'; } }catch{} return ()=>{ try{ if('scrollRestoration' in window.history) window.history.scrollRestoration=prev||'auto'; }catch{} }; },[]);
  useEffect(()=>{ const ok=ORDER_STATE_KEY(location.pathname); try{ savedOrderRef.current=JSON.parse(sessionStorage.getItem(ok)||'null'); }catch{ savedOrderRef.current=null; } },[location.pathname]);

  useEffect(()=>{
    const params=new URLSearchParams(location.search);
    params.set('sort',sortKey); params.set('page',String(page));
    history.replace({ pathname:location.pathname, search:params.toString() });
  },[sortKey,page,history,location.pathname,location.search]);

  useEffect(()=>{
    const fetchPU=async()=>{
      setLoading(true); setErr(null);
      try{
        const strict=`
          *[_type=="foto" && !(_id in path("drafts.**")) && (lower(categoria)=="${CATEGORIA}" || lower(categoria) match "paisajes*urbanos")]
          |order(_createdAt desc){
            _id,_createdAt,titulo,subcategoria,
            previewImage{asset->{url,metadata{dimensions{width,height,aspectRatio}}}},
            detalleImage{asset->{url,metadata{dimensions{width,height,aspectRatio}}}}
          }`;
        let data=await client.fetch(strict);
        if(!Array.isArray(data)||!data.length){
          const relaxed=`
            *[_type=="foto" && !(_id in path("drafts.**")) && lower(categoria) match "paisajes*urbanos"]|order(_createdAt desc){
              _id,_createdAt,titulo,subcategoria,
              previewImage{asset->{url,metadata{dimensions{width,height,aspectRatio}}}},
              detalleImage{asset->{url,metadata{dimensions{width,height,aspectRatio}}}}
            }`;
          data=await client.fetch(relaxed);
        }
        let items=normalizeDocs(data);
        items=await ensureOrientation(items);

        const orderKey=ORDER_STATE_KEY(location.pathname);
        const savedOrder=Array.isArray(savedOrderRef.current)?savedOrderRef.current:null;
        if(savedOrder && savedOrder.length){
          const pos=new Map(savedOrder.map((id,i)=>[id,i]));
          items.sort((a,b)=> (pos.get(a._id)??Number.MAX_SAFE_INTEGER) - (pos.get(b._id)??Number.MAX_SAFE_INTEGER) );
        }else{
          items=shuffle(items);
          try{ sessionStorage.setItem(orderKey, JSON.stringify(items.map(it=>it._id))); }catch{}
          savedOrderRef.current = items.map(it=>it._id);
        }

        setRawItems(items);
        itemsCountRef.current=items.length;
        const idx={}; items.forEach((it,i)=>{ idx[it._id]=i; }); rawIndexRef.current=idx;

        if(analytics) logEvent(analytics,'view_item_list',{item_list_name:TITLE, items_count:items.length});
      }catch(e){
        console.error(e); setErr(e?.message||'Error al cargar');
        setRawItems([]); rawIndexRef.current={}; itemsCountRef.current=0;
      }finally{ setLoading(false); }
    };
    fetchPU();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[location.pathname]);

  const sortedItems = useMemo(()=>{
    const favs=favoritos; let arr=[...rawItems];
    switch (sortKey){
      case 'recent': arr.sort((a,b)=>(b.createdAt||'').localeCompare(a.createdAt||'')); break;
      case 'az': arr.sort((a,b)=>(a.titulo||'').localeCompare(b.titulo||'',undefined,{sensitivity:'base'})); break;
      case 'za': arr.sort((a,b)=>(b.titulo||'').localeCompare(a.titulo||'',undefined,{sensitivity:'base'})); break;
      case 'liked':
        arr=arr.filter(it=>favs.has(it._id));
        arr.sort((a,b)=>(rawIndexRef.current[a._id]??0)-(rawIndexRef.current[b._id]??0));
        break;
      case 'random':
      default:
        arr.sort((a,b)=>(rawIndexRef.current[a._id]??0)-(rawIndexRef.current[b._id]??0));
        break;
    }
    return arr;
  },[rawItems, sortKey, favoritos]);

  const filteredItems = useMemo(()=>{
    if(!query) return sortedItems;
    return sortedItems.filter(it=>fuzzyIncludes(query, it.titulo||'') || fuzzyIncludes(query, it.subcategoria||''));
  },[sortedItems, query]);

  const allRows = useMemo(()=> buildRows(filteredItems).map(r=>r.filter(Boolean)).filter(r=>r.length>0), [filteredItems]);

  const visibleCountTarget = useMemo(()=>{
    if(sortKey==='liked') return filteredItems.length;
    return Math.min(page*PAGE_SIZE, filteredItems.length);
  },[filteredItems.length, page, sortKey]);

  const visibleRows = useMemo(()=>{
    let acc=0; const out=[];
    for(const row of allRows){ if(acc>=visibleCountTarget) break; out.push(row); acc+=row.length; }
    return out;
  },[allRows, visibleCountTarget]);

  useEffect(()=>{ setRows(visibleRows); },[visibleRows]);

  useEffect(()=>{ if(restoringSortRef.current){ restoringSortRef.current=false; return; } setPage(1); },[sortKey]);
  useEffect(()=>{ if(restoringQueryRef.current) return; setPage(1); },[query]);
  useEffect(()=>{ const t=setTimeout(()=>{ restoringQueryRef.current=false; },0); return ()=>clearTimeout(t); },[]);

  const handleClick=(id,title,index)=>{
    const key=GRID_STATE_KEY(location.pathname, location.search);
    const payload={ y:window.scrollY||0, page, sortKey, lastId:id, query, ts:Date.now() };
    try{ sessionStorage.setItem(key, JSON.stringify(payload)); }catch{}
    if(analytics) logEvent(analytics,'select_content',{content_type:'foto',content_id:id,content_title:title||'',list_name:'paisajesurbanos_grid',index});
    history.push(`/detalle/${id}`);
  };

  const handleToggleFav=(e,id,title)=>{
    e.stopPropagation(); e.preventDefault();
    if(!isLoggedIn()){ setShowLogin(true); if(analytics) logEvent(analytics,'login_prompt',{source:'paisajesurbanos_fav',content_id:id,content_title:title||''}); return; }
    setFavoritos(prev=>{
      const willAdd=!prev.has(id);
      const next=new Set(prev);
      if(willAdd) next.add(id); else next.delete(id);
      saveFavs(next);
      if(analytics) logEvent(analytics, willAdd?'add_to_favorites':'remove_from_favorites', {page:'paisajes-urbanos', content_type:'foto', content_id:id, content_title:title||''});
      return next;
    });
  };

  const atEnd = visibleCountTarget >= filteredItems.length;
  const canLoadMore = sortKey!=='liked' && !atEnd;
  const canLoadLess = sortKey!=='liked' && atEnd && page>1;

  const onChangeSort=(e)=>{ setSortKey(e.target.value); restoringSortRef.current=false; if(analytics) logEvent(analytics,'change_sort',{page:'paisajes-urbanos',sort:e.target.value}); };
  const onLoadMore=()=>{ setPage(p=>p+1); if(analytics) logEvent(analytics,'load_more',{page:'paisajes-urbanos',next_page:page+1}); };
  const onLoadLess=()=>{ setPage(p=>Math.max(1,p-1)); if(analytics) logEvent(analytics,'load_less',{page:'paisajes-urbanos',prev_page:Math.max(1,page-1)}); };

  // restauración de scroll
  useEffect(()=>{
    if(restoreOnceRef.current) return;
    if(desiredYRef.current==null) return;
    try{ if('scrollRestoration' in window.history) window.history.scrollRestoration='manual'; }catch{}
    const desiredY=Math.max(0, desiredYRef.current||0);
    const key=GRID_STATE_KEY(location.pathname, location.search);
    const tryScroll=()=>{
      const doc=document.scrollingElement||document.documentElement;
      const canReach=(doc.scrollHeight-window.innerHeight)>=(desiredY-4);
      if(canReach){
        window.scrollTo(0, desiredY);
        if(Math.abs(window.scrollY-desiredY)<=2){
          restoreOnceRef.current=true; desiredYRef.current=null; pendingIdRef.current=null;
          try{ sessionStorage.removeItem(key); }catch{}
          try{ if('scrollRestoration' in window.history) window.history.scrollRestoration='auto'; }catch{}
          return;
        }
      }
      restoreTriesRef.current+=1;
      if(restoreTriesRef.current===40 && pendingIdRef.current){
        const el=document.querySelector(`[data-id="${pendingIdRef.current}"]`);
        if(el) el.scrollIntoView({block:'center'});
      }
      if(restoreTriesRef.current>80){
        restoreOnceRef.current=true; desiredYRef.current=null; pendingIdRef.current=null;
        try{ sessionStorage.removeItem(key); }catch{}
        try{ if('scrollRestoration' in window.history) window.history.scrollRestoration='auto'; }catch{}
        return;
      }
      requestAnimationFrame(tryScroll);
    };
    requestAnimationFrame(tryScroll);
  },[rows, location.pathname, location.search]);

  return (
    <div className="pu-wrapper">
      <div className="pu-header">
        <h1 className="pu-title">{TITLE}</h1>
        <p className="pu-desc">{DESCRIPCION}</p>
      </div>

      <div className="pu-controls">
        <div className="pu-tabs">
          {SUBCATS.map(s=> <Link key={s.path} to={s.path} className="pu-tab">{s.label}</Link>)}
        </div>

        <div className="pu-sort">
          <label htmlFor="pu-sort-select" className="pu-sort-label">Ordenar por</label>
          <div className="pu-select-wrap">
            <select id="pu-sort-select" className="pu-sort-select" value={sortKey} onChange={onChangeSort}>
              <option value="random">Aleatorio</option>
              <option value="recent">Más recientes</option>
              <option value="az">A - Z</option>
              <option value="za">Z - A</option>
              <option value="liked">Likeados</option>
            </select>
            <span className="pu-select-caret" aria-hidden>▾</span>
          </div>
        </div>
        {/* búsqueda */}
        <div className="grid-search">
          <input
            type="search"
            className="grid-search-input"
            placeholder="Buscar por título…"
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
            aria-label="Buscar por título"
          />
          {query && <button type="button" className="grid-search-clear" onClick={()=>setQuery('')} aria-label="Limpiar búsqueda">×</button>}
        </div>
      </div>

      {loading ? (
        <p className="pu-info">Cargando…</p>
      ) : err ? (
        <p className="pu-info">Ups, algo salió mal: {err}</p>
      ) : rows.length===0 ? (
        <p className="pu-info">{query ? 'No hay resultados para tu búsqueda.' : 'Todavía no hay fotos para mostrar en esta categoría.'}</p>
      ) : (
        <>
          <div className="pu-rows-wrapper">
            {rows.map((row,rIdx)=>{
              const clean=row.filter(Boolean);
              const variant = clean.every(f=>f?.orientation===ORIENT.H) ? 'all-h' :
                              clean.every(f=>f?.orientation===ORIENT.V) ? 'all-v' : 'mixed';
              return (
                <div className={`pu-row pu-row-${clean.length} ${variant}`} key={`row-${rIdx}`}>
                  {clean.map((f,iIdx)=>{
                    const isFav=favoritos.has(f._id);
                    const cls=f.orientation===ORIENT.V?'tile v':'tile h';
                    ratioRef.current[f._id]=f.ratio||1;
                    const box=imgBox[f._id]||{w:0,h:0};
                    return (
                      <button
                        key={f._id||`${rIdx}-${iIdx}`} className={`pu-card ${cls}`}
                        data-id={f._id||`${rIdx}-${iIdx}`}
                        ref={(el)=>{ if(el) cardsRef.current[f._id||`${rIdx}-${iIdx}`]=el; }}
                        style={{'--ratio': f.ratio||1}}
                        onClick={()=>handleClick(f._id, f.titulo, iIdx)}
                        onContextMenu={(e)=>e.preventDefault()}
                        aria-label={`Ver ${f.titulo||'Foto'}`}
                      >
                        <div className="pu-img-viewport">
                          <div className="pu-img-box" style={{'--box-w':`${box.w||0}px`,'--box-h':`${box.h||0}px`}}>
                            <span className="pu-fav-toggle" onClick={(e)=>handleToggleFav(e,f._id,f.titulo)} role="button" tabIndex={0} aria-label={isFav?'Quitar de favoritos':'Agregar a favoritos'}>
                              {isFav ? (
                                <svg className="ri-heart-fill" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.001 4.529c2.349-2.109 5.979-2.04 8.242.228 2.263 2.268 2.34 5.88.236 8.236L12 21.485 3.521 12.993c-2.104-2.356-2.026-5.974.236-8.236 2.264-2.264 5.888-2.34 8.244-.228z"/></svg>
                              ) : (
                                <svg className="ri-heart-line" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12.001 4.52853C14.35 2.42 17.98 2.49 20.2426 4.75736C22.5053 7.02472 22.583 10.637 20.4786 12.993L11.9999 21.485L3.52138 12.993C1.41705 10.637 1.49571 7.01901 3.75736 4.75736C6.02157 2.49315 9.64519 2.41687 12.001 4.52853ZM18.827 6.1701C17.3279 4.66794 14.9076 4.60701 13.337 6.01687L12.0019 7.21524L10.6661 6.01781C9.09098 4.60597 6.67506 4.66808 5.17157 6.17157C3.68183 7.66131 3.60704 10.0473 4.97993 11.6232L11.9999 18.6543L19.0201 11.6232C20.3935 10.0467 20.319 7.66525 18.827 6.1701Z"/></svg>
                              )}
                            </span>
                            <img src={f.imageUrl} alt={f.titulo||'Foto'} className="pu-img" loading="lazy" draggable="false" onDragStart={(e)=>e.preventDefault()} onContextMenu={(e)=>e.preventDefault()} />
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {(canLoadMore || canLoadLess) && (
            <div className="pu-more-wrap">
              {!atEnd && <button type="button" className="pu-more-btn pu-more-primary" onClick={onLoadMore}>Ver más</button>}
              {atEnd && page>1 && <button type="button" className="pu-more-btn pu-less-btn" onClick={onLoadLess}>Ver menos</button>}
              <span className="pu-count">Mostrando {visibleCountTarget} / {filteredItems.length}</span>
            </div>
          )}

          {sortKey==='liked' && visibleCountTarget===0 && (
            <div className="pu-more-wrap"><span className="pu-count">No tenés favoritos en esta categoría todavía.</span></div>
          )}
        </>
      )}

      {/* Modal login */}
      <div className={`pu-login-modal ${showLogin ? 'open' : ''}`} aria-hidden={!showLogin}>
        <div className="pu-login-backdrop" onClick={()=>setShowLogin(false)} />
        <div className="pu-login-card" role="dialog" aria-modal="true" aria-labelledby="pu-login-title">
          <button className="pu-login-close" onClick={()=>setShowLogin(false)} aria-label="Cerrar">×</button>
          <h3 id="pu-login-title">Necesitás iniciar sesión</h3>
          <p>Para agregar favoritos tenés que estar logueado.</p>
          <Link to="/login" className="pu-login-link">Ir a login</Link>
        </div>
      </div>
    </div>
  );
};

export default PaisajesUrbanos;
